import { useCallback, useEffect, useRef } from "react";

const DRAFT_IDLE_MS = 2500;

function getStorage(name) {
  try {
    return window[name];
  } catch (error) {
    console.warn("[editor-draft] storage access failed", { name, error: error?.name, message: error?.message });
    return null;
  }
}

function storageSet(store, key, value) {
  try {
    store.setItem(key, value);
    return true;
  } catch (error) {
    console.warn("[editor-draft] storage write failed", { key, name: error?.name, message: error?.message });
    return false;
  }
}

function storageRemove(store, key) {
  try {
    store.removeItem(key);
  } catch (error) {
    console.warn("[editor-draft] storage remove failed", { key, name: error?.name, message: error?.message });
  }
}

export function readDraftStorage(draftId) {
  if (!draftId) return null;
  const key = `voiceEditorDraft:${draftId}`;
  for (const store of [getStorage("localStorage"), getStorage("sessionStorage")]) {
    try {
      const raw = store.getItem(key);
      if (raw) return raw;
    } catch (error) {
      console.warn("[editor-draft] storage read failed", { key, name: error?.name, message: error?.message });
    }
  }
  return null;
}

/**
 * Autosave editor draft to localStorage on idle (2.5s) or immediately when flushNow.
 */
export function useDraftAutosave({
  draftId,
  locked,
  title,
  content,
  deskId,
  drawerId,
  notebookId,
  onStatus,
}) {
  const timerRef = useRef(null);
  const statusTimerRef = useRef(null);
  const refs = useRef({ title, content, deskId, drawerId, notebookId });

  useEffect(() => {
    refs.current = { title, content, deskId, drawerId, notebookId };
  }, [title, content, deskId, drawerId, notebookId]);

  const persistDraft = useCallback(() => {
    if (!draftId || locked) return false;
    try {
      const { title: t, content: c, deskId: d, drawerId: dr, notebookId: n } = refs.current;
      const payload = {
        title: t,
        content: c,
        deskId: d,
        drawerId: dr,
        notebookId: n,
        timestamp: Date.now(),
      };
      const key = `voiceEditorDraft:${draftId}`;
      const serialized = JSON.stringify(payload);
      const localOk = storageSet(getStorage("localStorage"), key, serialized);
      const sessionOk = storageSet(getStorage("sessionStorage"), key, serialized);
      if (!localOk && !sessionOk) {
        console.error("[editor-draft] emergency write failed on both stores", { key });
        return false;
      }
      onStatus?.(localOk ? "Borrador local guardado" : "Borrador de emergencia guardado");
      if (statusTimerRef.current) clearTimeout(statusTimerRef.current);
      statusTimerRef.current = setTimeout(() => onStatus?.(""), 900);
      return true;
    } catch (e) {
      console.error("[editor-draft] unexpected save failure", { name: e?.name, message: e?.message });
      return false;
    }
  }, [draftId, locked, onStatus]);

  const scheduleDraftSave = useCallback(() => {
    if (!draftId || locked) return;
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(persistDraft, DRAFT_IDLE_MS);
  }, [draftId, locked, persistDraft]);

  const flushDraftSave = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    return persistDraft();
  }, [persistDraft]);

  useEffect(() => {
    scheduleDraftSave();
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [title, content, deskId, drawerId, notebookId, scheduleDraftSave]);

  useEffect(() => {
    return () => {
      if (statusTimerRef.current) clearTimeout(statusTimerRef.current);
    };
  }, []);

  return { flushDraftSave, scheduleDraftSave };
}

export function clearDraftStorage(draftId) {
  if (!draftId) return;
  const key = `voiceEditorDraft:${draftId}`;
  storageRemove(getStorage("localStorage"), key);
  storageRemove(getStorage("sessionStorage"), key);
  storageRemove(getStorage("localStorage"), "voiceEditorActiveDraftId");
  storageRemove(getStorage("sessionStorage"), "voiceEditorActiveDraftId");
}
